import React, { useState, useEffect } from 'react';
import componentStyles from './injuryReport.module.css';

const InjuryReport = ({ inDepthBetCardMatchupInjuries, sportType }) => {
    // State for managing active team selection
    const [activeTeam, setActiveTeam] = useState(null);
    const [teams, setTeams] = useState([]);

    // Helper function to get color for injury status
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'out':
                return 'var(--bloomberg-red)';
            case 'questionable':
            case 'doubtful':
                return 'var(--bloomberg-orange)';
            case 'probable':
                return 'var(--bloomberg-green)';
            default:
                return 'var(--bloomberg-white)';
        }
    };

    // Helper function to format table data for better display
    const formatTableCell = (key, value) => {
        // Check if this is a status column that needs color coding
        if (key.toLowerCase().includes('status')) {
            return (
                <span 
                    className={componentStyles.statusCell}
                    style={{ color: getStatusColor(value) }}
                >
                    {value}
                </span>
            );
        }
        
        // Check if this is a return date column
        if (key.toLowerCase().includes('return') || key.toLowerCase().includes('date')) {
            return <span className={componentStyles.dateCell}>{value}</span>;
        }
        
        // Check if this is a player name column
        if (key.toLowerCase().includes('player') || key.toLowerCase().includes('name')) {
            return <span className={componentStyles.playerCell}>{value}</span>;
        }
        
        // Default formatting
        return <span className={componentStyles.defaultCell}>{value}</span>;
    };

    // Filter injuries by selected team
    const getInjuriesForTeam = (teamName) => {
        if(Object.keys(inDepthBetCardMatchupInjuries).length === 0) {
            return [];
        }

        
        return inDepthBetCardMatchupInjuries[teamName];
    };

    // Display team selector buttons
    const displayTeamSelector = () => {

        // Set initial active team if none is selected
        if (!activeTeam && Object.keys(inDepthBetCardMatchupInjuries).length > 0) {
            setActiveTeam(Object.keys(inDepthBetCardMatchupInjuries)[0]);
        }

        const teamSelectorButtons = Object.keys(inDepthBetCardMatchupInjuries).map((team) => {
            const isActive = activeTeam === team;
            return (
                <button 
                    key={team}
                    className={`${componentStyles.teamSelectorButton} ${isActive ? componentStyles.teamSelectorButtonActive : ''}`}
                    onClick={() => setActiveTeam(team)}
                >
                    {team}
                </button>
            );
        });
        
        return (
            <div className={componentStyles.teamSelector}>
                <h3>Team Selector</h3>
                <div className={componentStyles.teamSelectorButtons}>
                    {teamSelectorButtons}
                </div>
            </div>
        );
    };

    // Display injury table for selected team
    const displayInjuryTable = () => {
        if (!activeTeam) {
            return (
                <div className={componentStyles.noDataMessage}>
                    Please select a team to view injury reports
                </div>
            );
        }

        const teamInjuries = getInjuriesForTeam(activeTeam);
        
        if (teamInjuries.length === 0) {
            return (
                <div className={componentStyles.noDataMessage}>
                    No injury data available for {activeTeam}
                </div>
            );
        }

        // Define which fields to display (filtering out internal/irrelevant data)
        const displayFields = [
            { key: 'player_name', label: 'Player Name' },
            { key: 'position', label: 'Position' },
            { key: 'status', label: 'Status' },
            { key: 'est_return_date', label: 'Est. Return Date' },
            { key: 'comment', label: 'Comment' }
        ];

        const tableHeader = displayFields.map((field) => {
            return (
                <th key={field.key} className={componentStyles.tableHeader}>
                    {field.label}
                </th>
            );
        });

        const tableRows = teamInjuries.map((injury, injuryIndex) => {   
            return (
                <tr key={injuryIndex} className={componentStyles.tableRow}>
                    {displayFields.map((field) => {
                        const value = injury[field.key] || '-';
                        return (
                            <td key={`${injuryIndex}-${field.key}`} className={componentStyles.tableCell}>
                                {formatTableCell(field.key, value)}
                            </td>
                        );
                    })}
                </tr>
            );
        });

        return (
            <div className={componentStyles.tableContainer}>
                <div className={componentStyles.tableWrapper}>
                    <table className={componentStyles.injuryTable}>
                        <thead>
                            <tr>{tableHeader}</tr>
                        </thead>
                        <tbody>{tableRows}</tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className={componentStyles.injuryReportContainer}>
            <div className={componentStyles.injuryReportHeader}>
                <h2>Injury Report</h2>
                {displayTeamSelector()}
            </div>
            {displayInjuryTable()}
        </div>
    );
};

export default InjuryReport;
